import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, Trash2, Save, X, ArrowLeft, Calendar as CalIcon, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";

// Hazır pansiyon konseptleri
const BOARDS = [
    "Ultra Her Şey Dahil",
    "Her Şey Dahil",
    "Alkolsüz Her Şey Dahil",
    "Yarım Pansiyon",
    "Tam Pansiyon",
    "Oda Kahvaltı",
    "Sadece Oda",
];

// Hazır iptal politikası şablonları
const CANCELLATION_TEMPLATES = [
    { key: "flexible", label: "Esnek", text: "Esnek - Giriş tarihinden 24 saat öncesine kadar ücretsiz iptal." },
    { key: "standard", label: "Standart", text: "Standart - Giriş tarihinden 3 gün öncesine kadar ücretsiz iptal." },
    { key: "strict", label: "Sıkı", text: "Sıkı - Giriş tarihinden 7 gün öncesine kadar %50 iade, sonrasında iade yok." },
    { key: "non_refundable", label: "İade Edilemez", text: "İade edilemez - Bu fiyatla yapılan rezervasyonlar iade edilmez." },
    { key: "manual", label: "Manuel (kendin yaz)", text: "" },
];

const EMPTY_PLAN = {
    name: "",
    cancellation_template: "flexible",
    cancellation: CANCELLATION_TEMPLATES[0].text,
    board: "Her Şey Dahil",
    min_stay: 1,
    room_type_ids: [],
    is_active: true,
    room_pricing_modes: [],
    child_rules: [{ child_index: 1, min_age: 0, max_age: 6, discount_percent: 0 }],
    child_room_overrides: [],
    // Comparison-table fields (kontrol bizde)
    description: "",
    badge: "",
    is_recommended: false,
    display_order: 0,
    advance_booking_days_min: 0,
};

export default function AdminRatePlansPage({
    Layout = AdminLayout,
    listRoute = "/admin/hotels",
    skipAuthCheck = false,
}) {
    const { id: hotelId } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth() || {};
    const [hotel, setHotel] = useState(null);
    const [plans, setPlans] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(null);
    const [selected, setSelected] = useState([]);
    const [error, setError] = useState("");

    const load = async () => {
        const [h, p] = await Promise.all([
            api.get(`/admin/hotels/${hotelId}`),
            api.get(`/admin/hotels/${hotelId}/rate-plans`),
        ]);
        setHotel(h.data);
        setPlans(p.data);
    };

    useEffect(() => {
        if (!skipAuthCheck) {
            if (authLoading) return;
            if (!user || !["super_admin", "admin", "otel_owner", "otel_manager"].includes(user.role)) return navigate("/login");
        }
        load();
    }, [user, authLoading, hotelId, skipAuthCheck]); // eslint-disable-line

    const startNew = () => {
        if (!hotel?.room_types?.length) { alert("Önce oda tipleri tanımlayın"); return; }
        setForm({ ...EMPTY_PLAN });
        setEditing("new");
        setError("");
    };

    const startEdit = (p) => {
        const tmpl = p.cancellation_template ||
            (CANCELLATION_TEMPLATES.find((t) => t.text === p.cancellation)?.key) || "manual";
        setForm({
            name: p.name,
            cancellation_template: tmpl,
            cancellation: p.cancellation,
            board: p.board,
            min_stay: p.min_stay ?? 1,
            room_type_ids: p.room_type_ids?.length ? p.room_type_ids : (p.room_type_id ? [p.room_type_id] : []),
            is_active: p.is_active !== false,
            room_pricing_modes: p.room_pricing_modes || [],
            child_rules: p.child_rules?.length ? p.child_rules : [{ child_index: 1, min_age: 0, max_age: 6, discount_percent: 0 }],
            child_room_overrides: p.child_room_overrides || [],
            description: p.description || "",
            badge: p.badge || "",
            is_recommended: !!p.is_recommended,
            display_order: p.display_order || 0,
            advance_booking_days_min: p.advance_booking_days_min || 0,
        });
        setEditing(p.id);
        setError("");
    };

    const cancel = () => { setForm(null); setEditing(null); setError(""); };

    const updateForm = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onTemplateChange = (key) => {
        const tmpl = CANCELLATION_TEMPLATES.find((t) => t.key === key);
        setForm((f) => ({
            ...f,
            cancellation_template: key,
            cancellation: key === "manual" ? f.cancellation : tmpl.text,
        }));
    };

    const toggleRoom = (rid) => {
        setForm((f) => {
            const isIn = f.room_type_ids.includes(rid);
            return {
                ...f,
                room_type_ids: isIn
                    ? f.room_type_ids.filter((x) => x !== rid)
                    : [...f.room_type_ids, rid],
                // When un-checking, also drop its pricing mode + override
                room_pricing_modes: isIn
                    ? (f.room_pricing_modes || []).filter((m) => m.room_type_id !== rid)
                    : (f.room_pricing_modes || []),
                child_room_overrides: isIn
                    ? (f.child_room_overrides || []).filter((o) => o.room_type_id !== rid)
                    : (f.child_room_overrides || []),
            };
        });
    };

    const allRoomsSelected = form && hotel?.room_types?.length === form.room_type_ids.length;
    const toggleAllRooms = () => {
        if (allRoomsSelected) updateForm("room_type_ids", []);
        else updateForm("room_type_ids", hotel.room_types.map((r) => r.id));
    };

    const save = async () => {
        setError("");
        if (!form.name?.trim()) { setError("Plan adı zorunludur"); return; }
        if (!form.room_type_ids?.length) { setError("En az 1 oda seçilmelidir"); return; }
        if (form.cancellation_template === "manual" && !form.cancellation?.trim()) {
            setError("Manuel iptal politikası metni boş olamaz"); return;
        }
        try {
            const payload = { ...form };
            if (editing === "new") {
                await api.post(`/admin/hotels/${hotelId}/rate-plans`, payload);
            } else {
                await api.patch(`/admin/hotels/${hotelId}/rate-plans/${editing}`, payload);
            }
            await load();
            cancel();
        } catch (e) {
            setError(e.response?.data?.detail || "Kaydetme başarısız");
        }
    };

    const onDelete = async (pid) => {
        if (!window.confirm("Fiyat planı silinsin mi? Takvim kayıtları da silinir.")) return;
        await api.delete(`/admin/hotels/${hotelId}/rate-plans/${pid}`);
        await load();
    };

    const toggleSelect = (pid) => setSelected((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
    const allSelected = plans.length > 0 && selected.length === plans.length;
    const toggleAll = () => setSelected(allSelected ? [] : plans.map((p) => p.id));

    const bulkAction = async (action, extra = {}) => {
        if (selected.length === 0) { alert("Lütfen plan seçin"); return; }
        if (action === "delete" && !window.confirm(`${selected.length} planı silmek istiyor musunuz?`)) return;
        try {
            await api.post(`/admin/hotels/${hotelId}/rate-plans/bulk-action`, { plan_ids: selected, action, ...extra });
            setSelected([]);
            await load();
        } catch (e) {
            alert(e.response?.data?.detail || "İşlem başarısız");
        }
    };

    if (!hotel) return <Layout title="Yükleniyor..."><div>Yükleniyor...</div></Layout>;

    return (
        <Layout
            title={`${hotel.name} - Fiyat Planları`}
            action={
                <div className="flex gap-2">
                    <Link to={`${listRoute}/${hotelId}/calendar`} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold">
                        <CalIcon className="h-4 w-4" /> Takvim
                    </Link>
                    <button onClick={() => navigate(listRoute)} className="px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-200">
                        <ArrowLeft className="h-4 w-4 inline -mt-0.5" /> Geri
                    </button>
                    <button data-testid="new-plan-btn" onClick={startNew} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold">
                        <Plus className="h-4 w-4" /> Yeni Fiyat Planı
                    </button>
                </div>
            }
        >
            {/* FORM */}
            {editing && form && (
                <div data-testid="plan-form" className="mb-6 p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-soft">
                    <h2 className="font-display text-xl font-bold mb-1 text-slate-900 dark:text-white">
                        {editing === "new" ? "Yeni Fiyat Planı Oluştur" : `Düzenle: ${form.name || "—"}`}
                    </h2>
                    <p className="text-sm text-slate-500 mb-5">Aşağıdaki adımları doldurun. Fiyatlar takvimden girilecektir.</p>
                    {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

                    <div className="space-y-6">
                        {/* 1 - Plan Adı */}
                        <Step number="1" title="Fiyat planı adı">
                            <input
                                data-testid="plan-name"
                                value={form.name}
                                onChange={(e) => updateForm("name", e.target.value)}
                                placeholder="Örn: Yaz Sezonu Standart"
                                className={INPUT}
                            />
                        </Step>

                        {/* 2 - İptal Politikası */}
                        <Step number="2" title="İptal politikası">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                {CANCELLATION_TEMPLATES.map((t) => (
                                    <button
                                        type="button"
                                        key={t.key}
                                        data-testid={`cancel-template-${t.key}`}
                                        onClick={() => onTemplateChange(t.key)}
                                        className={`p-3 rounded-xl border text-sm font-semibold transition-colors ${form.cancellation_template === t.key
                                            ? "bg-sky-50 dark:bg-sky-950/40 border-sky-500 text-sky-700 dark:text-sky-300"
                                            : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-sky-300"}`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                            {form.cancellation_template === "manual" ? (
                                <textarea
                                    data-testid="cancel-manual-text"
                                    rows={3}
                                    value={form.cancellation}
                                    onChange={(e) => updateForm("cancellation", e.target.value)}
                                    placeholder="Kendi iptal politikanızı yazın..."
                                    className={`${INPUT} mt-3`}
                                />
                            ) : (
                                <div className="mt-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/30 text-sm text-slate-600 dark:text-slate-300">
                                    {form.cancellation}
                                </div>
                            )}
                        </Step>

                        {/* 3 - Konsept */}
                        <Step number="3" title="Konsept (pansiyon)">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {BOARDS.map((b) => (
                                    <button
                                        type="button"
                                        key={b}
                                        data-testid={`board-${b}`}
                                        onClick={() => updateForm("board", b)}
                                        className={`p-2.5 rounded-xl border text-sm font-semibold transition-colors ${form.board === b
                                            ? "bg-amber-50 dark:bg-amber-950/30 border-amber-500 text-amber-700 dark:text-amber-300"
                                            : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-amber-300"}`}
                                    >
                                        {b}
                                    </button>
                                ))}
                            </div>
                        </Step>

                        {/* 4 - Min Konaklama */}
                        <Step number="4" title="Minimum konaklama (gece)">
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={() => updateForm("min_stay", Math.max(1, form.min_stay - 1))} className="h-10 w-10 rounded-xl border border-slate-300 dark:border-slate-600 text-lg font-bold">−</button>
                                <input
                                    data-testid="plan-min-stay"
                                    type="number"
                                    min="1"
                                    value={form.min_stay}
                                    onChange={(e) => updateForm("min_stay", Math.max(1, Number(e.target.value)))}
                                    className={`${INPUT} w-24 text-center`}
                                />
                                <button type="button" onClick={() => updateForm("min_stay", form.min_stay + 1)} className="h-10 w-10 rounded-xl border border-slate-300 dark:border-slate-600 text-lg font-bold">+</button>
                                <span className="text-sm text-slate-500 ml-2">gece</span>
                            </div>
                        </Step>

                        {/* 5 - Odalar */}
                        <Step number="5" title="Bu plana dahil olacak odalar">
                            <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs text-slate-500">{form.room_type_ids.length} / {hotel.room_types.length} oda seçili</span>
                                <button type="button" onClick={toggleAllRooms} className="text-xs font-semibold text-sky-500">{allRoomsSelected ? "Tümünü kaldır" : "Tümünü seç"}</button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {hotel.room_types.map((r) => {
                                    const checked = form.room_type_ids.includes(r.id);
                                    return (
                                        <label
                                            key={r.id}
                                            data-testid={`plan-room-${r.id}`}
                                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${checked
                                                ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-500"
                                                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:border-emerald-300"}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleRoom(r.id)}
                                                className="h-4 w-4 accent-emerald-500"
                                            />
                                            {r.image && <img src={r.image} alt="" className="h-10 w-14 object-cover rounded-lg" />}
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-slate-900 dark:text-white">{r.name}</div>
                                                <div className="text-xs text-slate-500">{r.capacity} kişilik · {r.size_sqm} m² · {r.bed_type}</div>
                                            </div>
                                            {checked && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                                        </label>
                                    );
                                })}
                            </div>

                            {/* Per-room pricing mode */}
                            {form.room_type_ids.length > 0 && (
                                <div className="mt-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/30 border border-dashed border-slate-200 dark:border-slate-700">
                                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Fiyatlandırma yöntemi</div>
                                    <p className="text-[11px] text-slate-500 mb-3">Her oda için fiyat girişi yöntemini seç. Takvim girişleri buna göre değişir.</p>
                                    <div className="space-y-2">
                                        {hotel.room_types
                                            .filter((rt) => form.room_type_ids.includes(rt.id))
                                            .map((rt) => {
                                                const mode = (form.room_pricing_modes || []).find((m) => m.room_type_id === rt.id);
                                                const m = mode?.mode || "manual";
                                                const cap = rt.capacity || 2;
                                                const setMode = (newMode) => {
                                                    const others = (form.room_pricing_modes || []).filter((x) => x.room_type_id !== rt.id);
                                                    const entry = { room_type_id: rt.id, mode: newMode };
                                                    if (newMode === "multiplier") {
                                                        // Default multipliers: linear by capacity
                                                        const mults = {};
                                                        for (let k = 1; k <= cap; k++) mults[String(k)] = +(k * 1.0).toFixed(2);
                                                        entry.multipliers = mode?.multipliers || mults;
                                                    }
                                                    updateForm("room_pricing_modes", [...others, entry]);
                                                };
                                                const setMult = (k, v) => {
                                                    const others = (form.room_pricing_modes || []).filter((x) => x.room_type_id !== rt.id);
                                                    const multipliers = { ...(mode?.multipliers || {}), [k]: v === "" ? "" : Number(v) };
                                                    updateForm("room_pricing_modes", [...others, { room_type_id: rt.id, mode: "multiplier", multipliers }]);
                                                };
                                                return (
                                                    <div key={rt.id} className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                                                        <div className="flex items-center gap-2 flex-wrap mb-2">
                                                            <span className="text-sm font-bold text-slate-900 dark:text-white">{rt.name}</span>
                                                            <span className="text-[11px] text-slate-500">({cap} kişilik)</span>
                                                            <div className="ml-auto inline-flex rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
                                                                {[
                                                                    { v: "manual", l: "Manuel" },
                                                                    { v: "multiplier", l: "Çarpanlı" },
                                                                    { v: "flat", l: "Ünite" },
                                                                ].map((opt) => (
                                                                    <button
                                                                        key={opt.v}
                                                                        type="button"
                                                                        onClick={() => setMode(opt.v)}
                                                                        data-testid={`pm-${rt.id}-${opt.v}`}
                                                                        className={`px-3 py-1 text-[11px] font-bold transition-colors ${m === opt.v ? "bg-sky-500 text-white" : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                                                                    >{opt.l}</button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        {m === "manual" && (
                                                            <p className="text-[10px] text-slate-500 italic">Takvimde her kapasite için ayrı fiyat girilir.</p>
                                                        )}
                                                        {m === "flat" && (
                                                            <p className="text-[10px] text-slate-500 italic">Takvimde tek fiyat girilir, tüm kapasiteler için aynı uygulanır.</p>
                                                        )}
                                                        {m === "multiplier" && (
                                                            <div className="flex items-end gap-2 flex-wrap">
                                                                <p className="text-[10px] text-slate-500 italic w-full mb-1">Takvimde tek "kişi başı" fiyat girilir; her kapasiteye aşağıdaki çarpanlar uygulanır. Örn: kişi başı 1000₺ × 2K çarpan 2.0 = 2000₺</p>
                                                                {Array.from({ length: cap }, (_, i) => i + 1).map((k) => (
                                                                    <div key={k} className="flex flex-col items-center gap-1">
                                                                        <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{k} kişi</span>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="0.1"
                                                                            value={mode?.multipliers?.[k] ?? ""}
                                                                            onChange={(e) => setMult(k, e.target.value)}
                                                                            placeholder="×"
                                                                            data-testid={`pm-mult-${rt.id}-${k}`}
                                                                            className="w-16 px-2 py-1 rounded bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-xs font-bold text-center text-sky-800 dark:text-sky-200"
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}
                        </Step>

                        {/* 6 - Çocuk Politikası */}
                        <Step number="6" title="Çocuk politikası">
                            <p className="text-xs text-slate-500 mb-3">
                                Her yaş aralığı için bir indirim oranı belirleyin. <b>İndirim %</b>, oda fiyatının <b>kişi başı payı</b>na uygulanır. Örn: 2 kişilik 2000₺ oda, %50 indirim → çocuk ücreti 500₺ (= 2000/2 × 0.5).
                            </p>
                            <ChildPolicyEditor
                                rules={form.child_rules || []}
                                onChange={(rules) => updateForm("child_rules", rules)}
                            />
                            {/* Per-room overrides */}
                            {form.room_type_ids.length > 1 && (
                                <div className="mt-5 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/30 border border-dashed border-slate-200 dark:border-slate-700">
                                    <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Oda bazlı özel kurallar (opsiyonel)</div>
                                    <p className="text-[11px] text-slate-500 mb-3">Belirli odalar için farklı çocuk politikası tanımlamak istersen aşağıdan ekle. Tanımlamadığın odalar için yukarıdaki varsayılan kurallar geçerli.</p>
                                    <div className="space-y-3">
                                        {hotel.room_types
                                            .filter((rt) => form.room_type_ids.includes(rt.id))
                                            .map((rt) => {
                                                const override = (form.child_room_overrides || []).find((o) => o.room_type_id === rt.id);
                                                return (
                                                    <div key={rt.id} className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className="text-sm font-bold text-slate-900 dark:text-white">{rt.name} <span className="text-[11px] font-normal text-slate-500">({rt.capacity} kişilik)</span></span>
                                                            {override ? (
                                                                <button type="button" onClick={() => updateForm("child_room_overrides", (form.child_room_overrides || []).filter((o) => o.room_type_id !== rt.id))} className="text-[11px] font-semibold text-red-600 hover:text-red-700">Özel kuralı kaldır</button>
                                                            ) : (
                                                                <button type="button" data-testid={`child-override-add-${rt.id}`} onClick={() => updateForm("child_room_overrides", [...(form.child_room_overrides || []), { room_type_id: rt.id, rules: [{ child_index: 1, min_age: 0, max_age: 6, discount_percent: 0 }] }])} className="text-[11px] font-semibold text-sky-600 hover:text-sky-700">+ Bu oda için özel kural</button>
                                                            )}
                                                        </div>
                                                        {override && (
                                                            <ChildPolicyEditor
                                                                rules={override.rules || []}
                                                                onChange={(rules) => {
                                                                    const others = (form.child_room_overrides || []).filter((o) => o.room_type_id !== rt.id);
                                                                    updateForm("child_room_overrides", [...others, { room_type_id: rt.id, rules }]);
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}
                        </Step>

                        {/* 7 - Aktif */}
                        <Step number="7" title="Plan durumu">
                            <button
                                type="button"
                                data-testid="plan-active-toggle"
                                onClick={() => updateForm("is_active", !form.is_active)}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border font-semibold text-sm ${form.is_active
                                    ? "bg-emerald-500 border-emerald-500 text-white"
                                    : "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"}`}
                            >
                                <span className={`h-2 w-2 rounded-full ${form.is_active ? "bg-white" : "bg-slate-400"}`} />
                                {form.is_active ? "AKTİF" : "PASİF"}
                            </button>
                            <p className="text-xs text-slate-500 mt-2">{form.is_active ? "Bu plan satışa açıktır." : "Bu plan satışta gösterilmeyecek."}</p>
                        </Step>

                        {/* 8 - Otel Detayı Karşılaştırma Tablosu Ayarları */}
                        <Step number="8" title="Karşılaştırma Tablosu (Otel Detayı)">
                            <p className="text-xs text-slate-500 mb-3">Bu plan otel detayında diğer planların yanında nasıl gözüksün? Hepsi opsiyoneldir.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <label className="block">
                                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Açıklama</span>
                                    <input data-testid="plan-description" value={form.description || ""} onChange={(e) => updateForm("description", e.target.value)} placeholder="örn. 30 gün öncesi · %25 indirim" className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm" />
                                    <span className="text-[10px] text-slate-400">Plan adının altında gri yazı olarak gözükür.</span>
                                </label>
                                <label className="block">
                                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Rozet (badge)</span>
                                    <input data-testid="plan-badge" value={form.badge || ""} onChange={(e) => updateForm("badge", e.target.value)} placeholder="örn. YENİ · POPÜLER · SINIRLI" className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm" />
                                    <span className="text-[10px] text-slate-400">Plan adının yanında mor rozet olarak gözükür.</span>
                                </label>
                                <label className="block">
                                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Minimum kaç gün öncesinden rezervasyon</span>
                                    <input data-testid="plan-advance-min" type="number" min="0" value={form.advance_booking_days_min || 0} onChange={(e) => updateForm("advance_booking_days_min", Math.max(0, Number(e.target.value)))} placeholder="0 = sınır yok" className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm" />
                                    <span className="text-[10px] text-slate-400">Erken Rezervasyon planları için. 0 = sınır yok. Örn. 30 yazarsanız, giriş tarihinden 30 gün önce kapanır.</span>
                                </label>
                                <label className="block">
                                    <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Tablo sıralaması</span>
                                    <input data-testid="plan-order" type="number" value={form.display_order || 0} onChange={(e) => updateForm("display_order", Number(e.target.value))} className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm" />
                                    <span className="text-[10px] text-slate-400">Küçük sayı üstte. Standart 0, Erken Rez. 1, vb.</span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border-2 border-slate-200 dark:border-slate-700 md:col-span-2 hover:border-sky-400 transition-colors">
                                    <input data-testid="plan-recommended" type="checkbox" checked={!!form.is_recommended} onChange={(e) => updateForm("is_recommended", e.target.checked)} className="h-5 w-5 accent-sky-500" />
                                    <div>
                                        <div className="font-semibold text-sm">⭐ "EN ÇOK SEÇİLEN" olarak işaretle</div>
                                        <div className="text-[11px] text-slate-500">Bu planı karşılaştırma tablosunda mavi vurgulu + büyük butonla öne çıkarır.</div>
                                    </div>
                                </label>
                            </div>
                        </Step>
                    </div>

                    <div className="flex items-center gap-2 mt-8 pt-6 border-t border-slate-100 dark:border-slate-800">
                        <button data-testid="save-plan" onClick={save} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold">
                            <Save className="h-4 w-4" /> Kaydet
                        </button>
                        <button onClick={cancel} className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-200">
                            <X className="h-4 w-4 inline -mt-0.5" /> Vazgeç
                        </button>
                    </div>
                </div>
            )}

            {/* BULK BAR */}
            {selected.length > 0 && (
                <div className="mb-4 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-sky-700 dark:text-sky-300">{selected.length} plan seçili:</span>
                    <button onClick={() => bulkAction("activate")} className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold">Aktifleştir</button>
                    <button onClick={() => bulkAction("deactivate")} className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-semibold">Pasifleştir</button>
                    <button onClick={() => bulkAction("delete")} className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-semibold">Sil</button>
                    <button onClick={() => setSelected([])} className="ml-auto text-xs text-slate-500">Seçimi temizle</button>
                </div>
            )}

            {/* LIST */}
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
                                <th className="py-3 px-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4 accent-sky-500" /></th>
                                <th className="py-3 px-3">Plan Adı</th>
                                <th className="py-3 px-3">Konsept</th>
                                <th className="py-3 px-3">Min Gece</th>
                                <th className="py-3 px-3">Odalar</th>
                                <th className="py-3 px-3">İptal</th>
                                <th className="py-3 px-3">Durum</th>
                                <th className="py-3 px-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.length === 0 ? (
                                <tr><td colSpan="8" className="py-10 text-center text-slate-500">Henüz fiyat planı yok. "Yeni Fiyat Planı" ile oluşturun.</td></tr>
                            ) : plans.map((p) => {
                                const roomNames = p.room_type_names || [p.room_type_name].filter(Boolean);
                                return (
                                    <tr key={p.id} data-testid={`plan-row-${p.id}`} className="border-b border-slate-100 dark:border-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                        <td className="py-3 px-3"><input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} className="h-4 w-4 accent-sky-500" /></td>
                                        <td className="py-3 px-3">
                                            <div className="font-semibold text-slate-900 dark:text-white">{p.name}</div>
                                            <div className="text-[10px] text-slate-400 font-mono">{p.code}</div>
                                        </td>
                                        <td className="py-3 px-3">
                                            <span className="px-2 py-1 rounded-md bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-xs font-semibold">{p.board}</span>
                                        </td>
                                        <td className="py-3 px-3 text-slate-700 dark:text-slate-300">{p.min_stay || 1} gece</td>
                                        <td className="py-3 px-3">
                                            <div className="flex flex-wrap gap-1 max-w-[260px]">
                                                {roomNames.slice(0, 3).map((n, i) => (
                                                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">{n}</span>
                                                ))}
                                                {roomNames.length > 3 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">+{roomNames.length - 3}</span>}
                                            </div>
                                        </td>
                                        <td className="py-3 px-3 text-xs text-slate-500 max-w-[180px] truncate">{p.cancellation}</td>
                                        <td className="py-3 px-3">
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${p.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>
                                                {p.is_active ? "Aktif" : "Pasif"}
                                            </span>
                                        </td>
                                        <td className="py-3 px-3 text-right">
                                            <button onClick={() => startEdit(p)} className="px-2 py-1 rounded-lg text-xs font-semibold bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 mr-1">Düzenle</button>
                                            <button onClick={() => onDelete(p.id)} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-4 w-4" /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
}

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white";

function Step({ number, title, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-2">
                <span className="h-7 w-7 rounded-full bg-sky-500 text-white text-xs font-bold flex items-center justify-center">{number}</span>
                <h3 className="font-display font-bold text-slate-900 dark:text-white">{title}</h3>
            </div>
            <div className="ml-9">{children}</div>
        </div>
    );
}

function ChildPolicyEditor({ rules, onChange }) {
    // Group rules by child_index
    const byChild = {};
    (rules || []).forEach((r) => {
        const k = r.child_index || 1;
        if (!byChild[k]) byChild[k] = [];
        byChild[k].push(r);
    });
    const childIndexes = Object.keys(byChild).map(Number).sort((a, b) => a - b);
    const maxChildIndex = childIndexes.length ? Math.max(...childIndexes) : 0;

    const ordinal = (n) => `${n}. Çocuk`;

    const setRule = (childIdx, ruleIdx, patch) => {
        const next = (rules || []).map((r) => r);
        let count = -1;
        for (let i = 0; i < next.length; i++) {
            if ((next[i].child_index || 1) === childIdx) {
                count++;
                if (count === ruleIdx) {
                    next[i] = { ...next[i], ...patch };
                    break;
                }
            }
        }
        onChange(next);
    };

    const addBracket = (childIdx) => {
        // find max max_age for this child, start new bracket after it
        const childRules = byChild[childIdx] || [];
        const lastMax = childRules.length ? Math.max(...childRules.map((r) => r.max_age || 0)) : -1;
        const next = [...(rules || []), { child_index: childIdx, min_age: lastMax + 1, max_age: lastMax + 5, discount_percent: 50 }];
        onChange(next);
    };

    const removeBracket = (childIdx, ruleIdx) => {
        const next = (rules || []).filter((r, _) => true); // we'll filter properly below
        const filtered = [];
        let count = -1;
        for (const r of rules || []) {
            if ((r.child_index || 1) === childIdx) {
                count++;
                if (count === ruleIdx) continue;
            }
            filtered.push(r);
        }
        onChange(filtered);
    };

    const addChild = () => {
        const newIdx = maxChildIndex + 1;
        onChange([...(rules || []), { child_index: newIdx, min_age: 0, max_age: 6, discount_percent: 50 }]);
    };

    const removeChild = (childIdx) => {
        onChange((rules || []).filter((r) => (r.child_index || 1) !== childIdx));
    };

    return (
        <div className="space-y-3">
            {childIndexes.map((ci) => {
                const childRules = byChild[ci];
                return (
                    <div key={ci} data-testid={`child-block-${ci}`} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
                        <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/40 flex items-center justify-between border-b border-slate-200 dark:border-slate-700">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">{ordinal(ci)}</span>
                            {childIndexes.length > 1 && (
                                <button type="button" onClick={() => removeChild(ci)} className="text-[11px] font-semibold text-red-500 hover:text-red-600">Kaldır</button>
                            )}
                        </div>
                        <div className="p-3 space-y-2">
                            {childRules.map((r, idx) => (
                                <div key={idx} className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[11px] font-semibold text-slate-500 min-w-[60px]">Yaş</span>
                                    <input
                                        type="number"
                                        min="0"
                                        max="18"
                                        value={r.min_age}
                                        onChange={(e) => setRule(ci, idx, { min_age: Number(e.target.value) })}
                                        data-testid={`child-min-${ci}-${idx}`}
                                        className="w-14 px-2 py-1 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-semibold text-center text-slate-900 dark:text-white"
                                    />
                                    <span className="text-[11px] text-slate-400">—</span>
                                    <input
                                        type="number"
                                        min="0"
                                        max="18"
                                        value={r.max_age}
                                        onChange={(e) => setRule(ci, idx, { max_age: Number(e.target.value) })}
                                        data-testid={`child-max-${ci}-${idx}`}
                                        className="w-14 px-2 py-1 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-semibold text-center text-slate-900 dark:text-white"
                                    />
                                    <span className="text-[11px] text-slate-500 ml-3">İndirim</span>
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="1"
                                            value={r.discount_percent}
                                            onChange={(e) => setRule(ci, idx, { discount_percent: Number(e.target.value) })}
                                            data-testid={`child-disc-${ci}-${idx}`}
                                            className="w-16 px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 text-sm font-bold text-center text-emerald-700 dark:text-emerald-300"
                                        />
                                        <span className="text-xs text-slate-500">%</span>
                                    </div>
                                    {childRules.length > 1 && (
                                        <button type="button" onClick={() => removeBracket(ci, idx)} className="ml-auto p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30" title="Yaş aralığını kaldır">
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button type="button" onClick={() => addBracket(ci)} data-testid={`add-bracket-${ci}`} className="text-[11px] font-semibold text-sky-600 hover:text-sky-700">+ Yaş aralığı ekle</button>
                        </div>
                    </div>
                );
            })}
            <button
                type="button"
                onClick={addChild}
                data-testid="add-child-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-100 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 text-xs font-bold hover:bg-sky-200 dark:hover:bg-sky-900/40"
            >
                <Plus className="h-3.5 w-3.5" /> {ordinal(maxChildIndex + 1)} ekle
            </button>
        </div>
    );
}
